/**
 * The workspace (tenant) this tab works in, when it is not the one the sign-in names (W3, ADR-0044).
 *
 * The chosen workspace travels as `X-Zagru-Tenant` on every API request; the server honours it only for a workspace
 * the person belongs to (or holds a support grant for) and refuses anything else. It is kept per tab
 * (`sessionStorage`) so two tabs can work in two customers side by side, and remembered (`localStorage`) as the
 * starting workspace of the next tab.
 */
export const WORKSPACE_HEADER = 'X-Zagru-Tenant';
const TAB_KEY = 'zagru.workspace.tab';
const LAST_KEY = 'zagru.workspace.last';

type Listener = (tenantId: string | null) => void;
const listeners = new Set<Listener>();
let active: string | null | undefined;

function read(storage: 'session' | 'local', key: string): string | null {
  try {
    const s = storage === 'session' ? window.sessionStorage : window.localStorage;
    return s.getItem(key);
  } catch {
    return null;
  }
}

function write(storage: 'session' | 'local', key: string, value: string | null): void {
  try {
    const s = storage === 'session' ? window.sessionStorage : window.localStorage;
    if (value) s.setItem(key, value);
    else s.removeItem(key);
  } catch {
    // storage unavailable: the choice lasts until reload
  }
}

/** The chosen workspace's tenant id, or `null` for the home workspace. */
export function getActiveWorkspaceId(): string | null {
  if (active === undefined) {
    active = typeof window === 'undefined' ? null : (read('session', TAB_KEY) ?? read('local', LAST_KEY));
  }
  return active;
}

/** Chooses the workspace for this tab (`null` = home) and remembers it for the next one. */
export function setActiveWorkspaceId(tenantId: string | null): void {
  active = tenantId;
  write('session', TAB_KEY, tenantId);
  write('local', LAST_KEY, tenantId);
  for (const l of listeners) l(tenantId);
}

export function subscribeWorkspace(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** The header the API client adds to every request. */
export function workspaceHeaders(): Record<string, string> {
  const id = getActiveWorkspaceId();
  return id ? { [WORKSPACE_HEADER]: id } : {};
}
